import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import '../../../styles/aida-base.css';
import '../../../styles/aida-how-it-works.css';
import '../../../styles/aida-responsive.css';

/**
 * HowItWorks Component
 * Three-step layout: Install App → Host Playcasts → Free Games
 * Desktop: Absolute-positioned 1024px canvas with animated phone decorations
 * Mobile: Vertically stacked centered blocks — image, title, body
 */

const WHITE = 'rgba(255, 255, 255, 1)';

const desktopTitleStyle: React.CSSProperties = {
  lineHeight: '25px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 900,
  fontSize: '25px',
  color: WHITE,
  margin: 0,
  padding: 0,
  textAlign: 'center',
  fontVariationSettings: "'wght' 900",
};

const desktopBodyStyle: React.CSSProperties = {
  lineHeight: '13px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '10px',
  color: WHITE,
  textAlign: 'center',
  margin: 0,
  padding: 0,
  fontVariationSettings: "'wght' 350",
};

const mobileTitleStyle: React.CSSProperties = {
  lineHeight: '38px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 900,
  fontSize: '34px',
  color: WHITE,
  margin: 0,
  textAlign: 'center',
  fontVariationSettings: "'wght' 900",
};

const mobileBodyStyle: React.CSSProperties = {
  lineHeight: '22px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  color: WHITE,
  textAlign: 'center',
  margin: 0,
};

export const HowItWorks = () => {
  const desktopRef = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(desktopRef, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });

  return (
    <>
      {/* ── Desktop Layout ── */}
      <motion.section
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          height: '490px',
          backgroundColor: 'transparent',
          paddingTop: '30px',
          overflow: 'visible',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          ref={desktopRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          style={{ position: 'relative', width: '1024px', height: '100%' }}
        >
          {/* Section header */}
          <div
            style={{
              position: 'absolute',
              left: '252px',
              top: '0px',
              width: '519px',
              height: '88px',
              zIndex: 370,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <h1
              style={{
                lineHeight: '44px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 900,
                fontSize: '37px',
                color: WHITE,
                margin: 0,
                textAlign: 'center',
                fontVariationSettings: "'wght' 900",
              }}
            >
              JOIN THE NETWORK AND EARN REWARDS FOR FREE
            </h1>
          </div>

          {/* Floating phone — top-left */}
          <motion.div
            className="absolute"
            style={{ left: '-352px', top: '-259px', width: '704px', height: '670px', zIndex: 314, pointerEvents: 'none' }}
            animate={{ y: [0, -20, 0], opacity: [0.5, 0.65, 0.5] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div
              style={{
                left: '40px',
                top: '160px',
                width: '624px',
                height: '351px',
                transform: 'rotate(-40deg) scaleX(-1)',
                position: 'relative',
              }}
            >
              <img
                src="/img/aida/phone-desktop-1.png"
                alt=""
                style={{ width: '100%', height: '100%', opacity: 0.6, objectFit: 'contain' }}
              />
            </div>
          </motion.div>

          {/* Floating phone — top-right */}
          <motion.div
            className="absolute"
            style={{ left: '679px', top: '-254px', width: '689px', height: '689px', zIndex: 315, pointerEvents: 'none' }}
            animate={{ y: [0, 15, 0], opacity: [0.5, 0.65, 0.5] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          >
            <div
              style={{
                left: '33px',
                top: '169px',
                width: '624px',
                height: '351px',
                transform: 'rotate(45deg)',
                position: 'relative',
              }}
            >
              <img
                src="/img/aida/phone-desktop-1.png"
                alt=""
                style={{ width: '100%', height: '100%', opacity: 0.6, objectFit: 'contain' }}
              />
            </div>
          </motion.div>

          {/* Floating phone — far right */}
          <motion.div
            className="absolute"
            style={{ left: '889px', top: '-35px', width: '271px', height: '311px', zIndex: 316, pointerEvents: 'none' }}
            animate={{ y: [0, -25, 0], opacity: [0.5, 0.65, 0.5] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
          >
            <div
              style={{
                left: '-12px',
                top: '30px',
                width: '294px',
                height: '251px',
                transform: 'rotate(86deg) scaleY(-1)',
                position: 'relative',
              }}
            >
              <img
                src="/img/aida/phone-desktop-2.png"
                alt=""
                style={{ width: '100%', height: '100%', opacity: 0.6, objectFit: 'contain' }}
              />
            </div>
          </motion.div>

          {/* Floating phone — far left */}
          <motion.div
            className="absolute"
            style={{ left: '-148px', top: '-156px', width: '297px', height: '481px', zIndex: 317, pointerEvents: 'none' }}
            animate={{ y: [0, 18, 0], opacity: [0.5, 0.65, 0.5] }}
            transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
          >
            <div
              style={{
                left: '-79px',
                top: '119px',
                width: '455px',
                height: '243px',
                transform: 'rotate(263deg) scaleX(-1) scaleY(-1)',
                position: 'relative',
              }}
            >
              <img
                src="/img/aida/phone-desktop-3.png"
                alt=""
                style={{ width: '100%', height: '100%', opacity: 0.6, objectFit: 'contain' }}
              />
            </div>
          </motion.div>

          {/* Icon Block 1: Install App */}
          <div
            style={{
              position: 'absolute',
              left: '143px',
              top: '108px',
              width: '203px',
              height: '300px',
              zIndex: 360,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div style={{ width: '203px', height: '195px' }}>
              <img
                src="/img/aida/icon-install-app.png"
                alt="Install App"
                style={{ width: '100%', height: '100%', opacity: 1, objectFit: 'contain' }}
              />
            </div>
            <div style={{ marginTop: '18px' }}>
              <h2 style={desktopTitleStyle}>Install App</h2>
            </div>
            <div style={{ marginTop: '12px', width: '171px' }}>
              <p style={desktopBodyStyle}>
                Install, log in, and get to hosting with your computer.
              </p>
            </div>
          </div>

          {/* Icon Block 2: Host Playcasts */}
          <div
            style={{
              position: 'absolute',
              left: '424px',
              top: '121px',
              width: '189px',
              height: '300px',
              zIndex: 360,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div style={{ width: '189px', height: '167px' }}>
              <img
                src="/img/aida/icon-host-playcasts.png"
                alt="Host Playcasts"
                style={{ width: '100%', height: '100%', opacity: 1, objectFit: 'contain' }}
              />
            </div>
            <div style={{ marginTop: '28px' }}>
              <h2 style={{ ...desktopTitleStyle, whiteSpace: 'nowrap' }}>Power the Network</h2>
            </div>
            <div style={{ marginTop: '12px', width: '156px' }}>
              <p style={desktopBodyStyle}>
                Enable play from your machine in a secure, sand-boxed environment.
              </p>
            </div>
          </div>

          {/* Icon Block 3: Free Games */}
          <div
            style={{
              position: 'absolute',
              left: '700px',
              top: '131px',
              width: '180px',
              height: '300px',
              zIndex: 360,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div style={{ width: '172px', height: '157px' }}>
              <img
                src="/img/aida/HowItWorks/us2Z-HBHpScYhDjQ9QixA.png"
                alt="Earn Rewards"
                style={{ width: '100%', height: '100%', opacity: 1, objectFit: 'contain' }}
              />
            </div>
            <div style={{ marginTop: '28px', width: '208px' }}>
              <h2 style={desktopTitleStyle}>Earn Rewards</h2>
            </div>
            <div style={{ marginTop: '12px', width: '136px' }}>
              <p style={desktopBodyStyle}>
                While your PC is idle, it's earning you IRL rewards.
              </p>
            </div>
          </div>
        </motion.div>
      </motion.section>

      {/* ── Mobile Layout — stacked: image → title → body ── */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          overflow: 'hidden',
          padding: '16px 20px 40px',
          gap: '48px',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Section header */}
        <h1
          style={{
            lineHeight: '36px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 900,
            fontSize: '30px',
            color: WHITE,
            margin: 0,
            textAlign: 'center',
            fontVariationSettings: "'wght' 900",
          }}
        >
          JOIN THE NETWORK AND EARN REWARDS FOR FREE
        </h1>

        {/* Block 1: Install App */}
        <motion.div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, delay: 0, ease: 'easeOut' }}
        >
          <div style={{ width: '305px', height: '293px' }}>
            <img
              src="/img/aida/icon-install-app.png"
              alt="Install App"
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          </div>
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <h2 style={mobileTitleStyle}>Install App</h2>
          </div>
          <p style={{ ...mobileBodyStyle, width: '300px', marginTop: '9px' }}>
            Install, log in, and get to hosting with your computer.
          </p>
        </motion.div>

        {/* Block 2: Host Playcasts */}
        <motion.div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, delay: 0.1, ease: 'easeOut' }}
        >
          <div style={{ width: '284px', height: '251px', marginLeft: '60px' }}>
            <img
              src="/img/aida/icon-host-playcasts.png"
              alt="Host Playcasts"
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          </div>
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <h2 style={mobileTitleStyle}>Power the Network</h2>
          </div>
          <p style={{ ...mobileBodyStyle, width: '300px', marginTop: '9px' }}>
            Enable play from your machine in a secure, sand-boxed environment.
          </p>
        </motion.div>

        {/* Block 3: Free Games */}
        <motion.div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, delay: 0.2, ease: 'easeOut' }}
        >
          <div style={{ width: '258px', height: '236px' }}>
            <img
              src="/img/aida/HowItWorks/us2Z-HBHpScYhDjQ9QixA.png"
              alt="Earn Rewards"
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          </div>
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <h2 style={mobileTitleStyle}>Earn Rewards</h2>
          </div>
          <p style={{ ...mobileBodyStyle, width: '300px', marginTop: '9px' }}>
            While your PC is idle, it's earning you IRL rewards.
          </p>
        </motion.div>
      </motion.section>
    </>
  );
};
